<template>
	<div class="assembly-panel">
		<div class="header">
			<h4>{{ t("landAssembly.componentTransactions") }}</h4>
			<button type="button" class="close-btn" :title="t('callToActions.close')" @click="emit('close')">
				<AppIcon :name="IconName.CloseSmall" :size="16" :stroke-width="1.3" />
			</button>
		</div>
		<div class="separator"></div>
		<form class="add-form" @submit.prevent="handleSubmit">
			<input
				v-model="inscriptionNumber"
				type="text"
				:placeholder="t('landAssembly.inscriptionNumberPlaceholder')"
				:disabled="adding || removing"
				@input="emit('clear-error')"
			/>
			<Button
				type="submit"
				class="add-btn"
				size="s"
				variant="primary"
				:disabled="adding || removing || !inscriptionNumber.trim()"
			>
				{{ t("landAssembly.add") }}
			</Button>
		</form>
		<div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>
		<div class="items">
			<div v-if="!components.length" class="empty-state">{{ t("landAssembly.noComponents") }}</div>
			<TransactionCard
				v-for="component in components"
				:key="component.inscriptionNumber ?? 0"
				class="item"
				:transaction="component"
				removable
				:remove-disabled="removing"
				@remove="emit('remove', component)"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
import { useI18n } from "vue-i18n"
import AppIcon from "@/components/AppIcon.vue"
import { IconName } from "@/components/icons/registry"
import type { Transaction } from "@/model/DataModel.ts"
import Button from "@/components/ui/Button.vue"
import TransactionCard from "./TransactionCard.vue"

defineProps<{
	components: Transaction[]
	adding?: boolean
	removing?: boolean
	errorMessage?: string
}>()

const emit = defineEmits<{
	(e: "add", inscriptionNumber: string): void
	(e: "remove", component: Transaction): void
	(e: "clear-error"): void
	(e: "close"): void
}>()

const inscriptionNumber = defineModel<string>("inscriptionNumber", { default: "" })

const { t } = useI18n()

function handleSubmit() {
	const trimmed = inscriptionNumber.value.trim()
	if (!trimmed) return
	emit("add", trimmed)
}
</script>

<style scoped>
.assembly-panel {
	position: fixed;
	top: 0;
	right: 0;
	width: var(--width-transaction-export-panel);
	height: 100%;
	background-color: var(--neutral-0);
	border-left: var(--border-width-thin) solid var(--neutral-100);
	z-index: 10;
	overflow: hidden;
	display: flex;
	flex-direction: column;
	gap: var(--spacing-sm);
	padding: var(--spacing-md) var(--spacing-sm);
	font-size: var(--font-size-small);

	.header {
		display: flex;
		justify-content: space-between;
		align-items: center;

		h4 {
			font-size: var(--font-size-base);
			font-family: var(--font-family-primary);
			font-weight: var(--font-weight-medium);
			color: var(--neutral-900);
			margin: 0;
		}

		.close-btn {
			flex-shrink: 0;
			width: 20px;
			height: 20px;
			display: flex;
			align-items: center;
			justify-content: center;
			padding: 0;
			border: none;
			background: none;
			border-radius: var(--border-radius-sm);
			color: var(--neutral-600);
			cursor: pointer;
			transition: all 0.2s ease;

			&:hover {
				color: var(--primary-green);
				background-color: var(--neutral-200);
			}
		}
	}

	.separator {
		width: 100%;
		height: var(--border-width-thin);
		background-color: var(--neutral-400);
	}

	.add-form {
		display: flex;
		gap: var(--spacing-sm);

		input {
			flex: 1;
			min-width: 0;
			padding: 0 var(--spacing-sm);
			height: 24px;
			border: var(--border-width-thin) solid var(--neutral-400);
			border-radius: var(--border-radius-base);
			font-family: var(--font-family-primary);
			font-size: var(--font-size-small);
			color: var(--neutral-900);
			background-color: var(--neutral-0);

			&:focus {
				outline: none;
				border-color: var(--primary-green);
			}

			&:disabled {
				background-color: var(--neutral-50);
				cursor: not-allowed;
			}
		}
	}

	.error-message {
		font-family: var(--font-family-primary);
		font-size: var(--font-size-small);
		color: var(--accent-warning);
	}

	.items {
		flex: 1;
		overflow-y: auto;
		display: flex;
		flex-direction: column;
		gap: var(--spacing-xs);

		.empty-state {
			font-family: var(--font-family-primary);
			font-size: var(--font-size-small);
			color: var(--neutral-600);
		}

		.item {
			margin-right: var(--spacing-xs);
			border: var(--border-width-thin) solid var(--neutral-200);
			border-radius: var(--border-radius-md);
		}
	}
}

.add-form :deep(.add-btn) {
	min-width: 0;
	width: fit-content;
}
</style>
